import React from 'react'
import { MapPin } from 'lucide-react'
import { usePrepareCart } from '../../../../../hooks/usePrepareCart'
import { CouponSection, FreeShippingProgress, PaymentMethods } from '../../../../../new-components/CartSheet/components'
import { calculateShippingCost } from '../../../../../utils/constants'
import { Sumary } from '../Sumary/Sumary'
import { RecurrencyCartSection } from '@cannect/new-components/organisms/Recurrency/RecurrencyCartSection'

interface CartItem {
  product?: {
    current_price?: number
  }
  quantity?: number
}

interface OrderSummaryProps {
  itens: CartItem[]
  importCost?: number
  onApplyCoupon: (coupon: any, itens: CartItem[]) => void
  onProceedToPayment: () => void
  isUpdate: boolean
  coupon: any
  onRemoveCoupon: () => void
  lastOrderCreated: any
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  itens,
  importCost,
  onApplyCoupon,
  onProceedToPayment,
  isUpdate,
  coupon,
  onRemoveCoupon,
  lastOrderCreated
}) => {
  const { loading, cartLoading, deliveryCep, setDeliveryCep, validateDeliveryCep, validatingDeliveryCep } =
    usePrepareCart()
  const cartItems = lastOrderCreated?.items || []
  const subtotal = lastOrderCreated?.subtotal || 0
  const noImportCost = lastOrderCreated?.noImportCost || false

  const shippingRestriction =
    lastOrderCreated?.shipping_restriction ?? lastOrderCreated?.summary?.shipping_restriction ?? null
  const tazBlocked =
    shippingRestriction !== null &&
    (shippingRestriction.covered === false || (shippingRestriction.requires_cep && !deliveryCep))
  const hasUnavailableItems = !!(lastOrderCreated?.summary?.has_unavailable_items ?? lastOrderCreated?.has_unavailable_items)
  const unavailableItems: { product_id: number; name: string }[] =
    lastOrderCreated?.summary?.unavailable_items ?? lastOrderCreated?.unavailable_items ?? []

  const calculatedImportCost = importCost ?? calculateShippingCost(cartItems)

  const freeShippingThreshold = 2500
  const freeShippingMessage = 'Faltam {amount} para Custo de importação grátis'

  const handleCepChange = (value: string) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 8)
    const masked = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits
    setDeliveryCep(masked)
  }

  if (loading) {
    return (
      <div className="h-auto w-full rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="space-y-7">
          <div className="h-6 w-32 animate-pulse rounded bg-gray-200" />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
            <div className="h-16 w-full animate-pulse rounded bg-gray-200" />
          </div>
          <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
          <div className="flex w-full justify-center">
            <div className="h-8 w-32 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-auto w-full rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
      <div className="space-y-6">
        <Sumary itens={itens} importCost={calculatedImportCost} selectedPaymentMethod="" />

        <RecurrencyCartSection />

        <div className="space-y-2">
          <CouponSection
            onApplyCoupon={onApplyCoupon}
            onRemoveCoupon={onRemoveCoupon}
            coupon={coupon}
            itens={itens}
            isUpdating={isUpdate}
          />

          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Digite seu CEP:"
              className="w-[calc(100%-90px)] rounded-md border-0 bg-[#EDECE7] py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6B7B3C] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={deliveryCep || ''}
              onChange={(e) => handleCepChange(e.target.value)}
              disabled={isUpdate || validatingDeliveryCep}
            />
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              <button
                className="rounded-md border-none bg-primary-700 px-3 py-2 text-sm text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                onClick={validateDeliveryCep}
                disabled={isUpdate || validatingDeliveryCep || (deliveryCep || '').replace(/\D/g, '').length !== 8}>
                {validatingDeliveryCep ? 'Validando...' : 'Validar'}
              </button>
            </div>
          </div>

          {!noImportCost && (
            <FreeShippingProgress subtotal={subtotal} threshold={freeShippingThreshold} message={freeShippingMessage} />
          )}
        </div>

        {hasUnavailableItems && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <p className="mb-1 font-semibold">Carrinho bloqueado — itens indisponíveis</p>
            <p className="mb-1">Remova os itens abaixo para continuar:</p>
            {unavailableItems.length > 0 && (
              <ul className="ml-3 list-disc">
                {unavailableItems.map((p) => (
                  <li key={p.product_id}>{p.name}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {shippingRestriction?.covered === false && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <p className="mb-1 font-semibold">Infelizmente não conseguimos entregar este produto na sua região.</p>
            <p className="mb-1">Os itens abaixo exigem transporte especial que não atende ao CEP informado.</p>
            {shippingRestriction.blocked_products?.length > 0 && (
              <ul className="ml-3 list-disc">
                {shippingRestriction.blocked_products.map((p: { product_id: number; name: string }) => (
                  <li key={p.product_id}>{p.name}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {shippingRestriction?.requires_cep && !deliveryCep && (
          <p className="text-xs text-amber-700">
            Informe o CEP para verificar a disponibilidade de transporte antes de avançar.
          </p>
        )}

        <button
          onClick={onProceedToPayment}
          disabled={cartLoading || isUpdate || itens.length === 0 || tazBlocked || hasUnavailableItems}
          className="h-10 w-full cursor-pointer rounded border-none bg-primary-800 px-16 py-2 text-base font-semibold text-white transition-colors hover:bg-[#5a6b2f] disabled:cursor-not-allowed disabled:bg-gray-400">
          {cartLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Processando...
            </div>
          ) : (
            'Continuar'
          )}
        </button>

        <div className="flex w-full justify-center">
          <PaymentMethods />
        </div>
      </div>
    </div>
  )
}
